import {
  Button,
  Flex,
  Image,
  Spinner,
  Text,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { DEFAULT_PARCEL_METHOD } from "@syla/shared/types/helpers/constants";
import { permissionFor } from "@syla/shared/types/helpers/permissions";
import {
  AccountSubType,
  AccountType,
  ParcelMethod,
} from "@syla/shared/types/models/UserBase";
import React, { useContext, useMemo, useEffect } from "react";
import { isBrowser } from "react-device-detect";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { deleteUser } from "../api/user/deleteUser";
import { getUser } from "../api/user/getUser";
import { resetUser } from "../api/user/resetUser";
import { updateUser } from "../api/user/updateUser";
import {
  PageContainer,
  PageHeadingContainer,
} from "../components/atoms/Containers";
import { PageHeading } from "../components/atoms/Headings";
import { errorToastOptions } from "../components/molecules/errorToastOptions";
// import { ABNForm } from "../components/molecules/forms/settingForms/ABNForm";
import { AccountTypeForm } from "../components/molecules/forms/settingForms/AccountTypeForm";
import { ActivityTypeForm } from "../components/molecules/forms/settingForms/ActivityTypeForm";
import { AddressForm } from "../components/molecules/forms/settingForms/AddressForm";
import { ContactNumberForm } from "../components/molecules/forms/settingForms/ContactNumberForm";
// import { EntityNameForm } from "../components/molecules/forms/settingForms/EntityName";
import { FullNameForm } from "../components/molecules/forms/settingForms/FullNameForm";
import { ParcelMethodForm } from "../components/molecules/forms/settingForms/ParcelMethodForm";
// import { TimeZoneForm } from "../components/molecules/forms/settingForms/TimeZoneForm";
import { UserEmailForm } from "../components/molecules/forms/settingForms/UserEmailForm";
import { UserPasswordForm } from "../components/molecules/forms/settingForms/UserPasswordForm";
import { SettingRow } from "../components/organisms/setting/SettingRow";
import { ModalContext } from "../contexts/ModalContext";
import { UserContext } from "../contexts/UserContext";
import { isProduction } from "../helper/environment";
import { useGetUserFinancialYearsData } from "../store/actions/userFinancialYear";
import { resetTransactionStore } from "../store/transactionsStore";
import { SettingsSubscriptionStatus } from "./SettingsSubscriptionStatus";

export const Settings = () => {
  const { logout } = useContext(UserContext);
  const { openModal, setBtn2Loading, closeModal } = useContext(ModalContext);

  // fetch user details
  const {
    isLoading: userDetailsIsLoading,
    isError: userDetailsIsError,
    data: userDetailsData,
    refetch: refetchUserDetails,
    isStale: userDetailsStale,
  } = useQuery("user-details", () => getUser());

  const {
    data: userFinancialYearsData,
    isLoading: isLoadingFinancialYearsData,
  } = useGetUserFinancialYearsData();

  const queryClient = useQueryClient();

  const {
    isLoading: updateUserInProgress,
    mutateAsync: updateUserRequest,
    error: updateUserError,
  } = useMutation(updateUser, {
    onSettled: () => {
      queryClient.invalidateQueries("user-details");
    },
  });

  const toast = useToast();

  useEffect(() => {
    if (updateUserError) {
      toast({
        ...errorToastOptions,
        position: "top",
        title: "Problem saving",
        description:
          "Please try again later or contact support if problems persist",
      });
    }
  }, [toast, updateUserError]);

  const defaultParcelMethod =
    userDetailsData?.taxSettings.defaultParcelMethod ?? DEFAULT_PARCEL_METHOD;

  const allUsersParcelMethods: ParcelMethod[] = useMemo(
    () =>
      (userFinancialYearsData ?? []).map((financialYear): ParcelMethod => {
        const parcelMethod = userDetailsData?.taxSettings.parcelMethods.find(
          (method) =>
            method.startYear ===
            financialYear.startDate.getUTCFullYear().toString()
        );

        return {
          startYear: financialYear.startDate.getUTCFullYear().toString(),
          endYear: financialYear.endDate.getUTCFullYear().toString(),
          method: parcelMethod?.method ?? defaultParcelMethod,
        };
      }),
    [
      defaultParcelMethod,
      userDetailsData?.taxSettings.parcelMethods,
      userFinancialYearsData,
    ]
  );

  const { onCopy, hasCopied } = useClipboard(userDetailsData?._id ?? "");

  const parcelPermission = useMemo(
    () =>
      permissionFor({
        action: "feat-tax-ltfo",
        subscriptions: userDetailsData?.subscriptions ?? [],
        userTransactionCount: 0,
        user: userDetailsData,
      }).hasPermission,
    [userDetailsData?.subscriptions]
  );

  // TODO: implement error toast for fetching user details

  if (userDetailsIsLoading || isLoadingFinancialYearsData)
    return (
      <PageContainer isBrowser={isBrowser}>
        <Flex justifyContent="center" my="40px">
          <Spinner />
        </Flex>
      </PageContainer>
    );

  if (userDetailsIsError || !userDetailsData)
    return (
      <PageContainer isBrowser={isBrowser}>
        <Flex alignItems="center" direction="column" my="40px">
          <Text textAlign="center">
            There was an error fetching user data, try again?
          </Text>
          <Button
            colorScheme="blue"
            onClick={() => {
              refetchUserDetails();
            }}
            size="sm"
            variant="link"
            w="100px"
          >
            Refresh
          </Button>
        </Flex>
      </PageContainer>
    );

  return (
    <PageContainer isBrowser={isBrowser}>
      <PageHeadingContainer>
        <PageHeading>Settings</PageHeading>
      </PageHeadingContainer>
      <Flex
        direction={isBrowser ? { base: "column", xl: "row" } : "column"}
        justifyContent={
          isBrowser ? { base: "center", xl: "space-between" } : "center"
        }
      >
        {/* ---------------------- LHS ---------------------- */}
        <Flex
          direction="column"
          w={isBrowser ? { base: "100%", xl: "48%" } : "100%"}
          maxW={isBrowser ? { base: "600px", xl: "800px" } : "600px"}
        >
          {/* ---------------------- Account Details ---------------------- */}
          <SectionContainer direction="column" minW="300px">
            <SectionHeading>Account Details</SectionHeading>
            <SettingRow
              label="Account Type"
              currentValue={userDetailsData.accountType}
              type="edit"
              action={() =>
                openModal({
                  type: "component",
                  heading: "Change Account Type",
                  component: (
                    <AccountTypeForm
                      currentAccountType={
                        AccountType[userDetailsData.accountType]
                      }
                    />
                  ),
                })
              }
            />
            <SettingRow
              label="Activity Type"
              currentValue={userDetailsData.accountSubType}
              action={() =>
                openModal({
                  type: "component",
                  heading: "Change Activity Type",
                  component: (
                    <ActivityTypeForm
                      currentActivityType={
                        AccountSubType[userDetailsData.accountSubType]
                      }
                    />
                  ),
                })
              }
            />
            <SettingRow
              label="BGL Account ID"
              currentValue={userDetailsData._id}
              btnText={hasCopied ? "copied" : "copy"}
              action={onCopy}
              type="copy"
            />
            <SettingRow
              label="Email"
              currentValue={userDetailsData.email}
              action={() =>
                openModal({
                  type: "component",
                  heading: "Change Email",
                  component: (
                    <UserEmailForm currentValue={userDetailsData.email} />
                  ),
                })
              }
            />
            <SettingRow
              label="Password"
              currentValue="******"
              action={() =>
                openModal({
                  type: "component",
                  heading: "Change Password",
                  component: <UserPasswordForm />,
                })
              }
              type="edit"
            />
            <SettingRow
              label="Full Name"
              currentValue={userDetailsData.fullName || ""}
              action={() =>
                openModal({
                  type: "component",
                  heading: "Full Name",
                  contents: [
                    "Full name is optional and only required if you want it displayed on your tax report",
                  ],
                  component: (
                    <FullNameForm
                      currentValue={userDetailsData.fullName || ""}
                    />
                  ),
                })
              }
              type="edit"
            />
            {/*
            <SettingRow
              label="Entity Name"
              currentValue={userDetailsData.entityName || ""}
              action={() =>
                openModal({
                  type: "component",
                  heading: "Entity Name",
                  contents: [
                    "Entity name is optional and only required if you want it displayed on your tax report",
                  ],
                  component: (
                    <EntityNameForm
                      currentValue={userDetailsData.entityName || ""}
                    />
                  ),
                })
              }
              type="edit"
            />
            <SettingRow
              label="ABN"
              currentValue={userDetailsData.abn || ""}
              action={() =>
                openModal({
                  type: "component",
                  heading: "ABN",
                  component: (
                    <ABNForm currentValue={userDetailsData.abn || ""} />
                  ),
                })
              }
              type="edit"
            />
            */}
            <SettingRow
              label="Contact Number"
              currentValue={
                userDetailsData.contactNumber.number
                  ? `${
                      userDetailsData.contactNumber.countryCode
                        ? `(${userDetailsData.contactNumber.countryCode})`
                        : ""
                    } ${userDetailsData.contactNumber.number}`
                  : ""
              }
              action={() =>
                openModal({
                  type: "component",
                  heading: "Contact Number",
                  contents: [
                    "Contact number is optional and only required if you want it displayed on your tax report",
                  ],
                  component: (
                    <ContactNumberForm
                      currentCountryCode={
                        userDetailsData.contactNumber.countryCode || ""
                      }
                      currentNumber={userDetailsData.contactNumber.number || ""}
                    />
                  ),
                })
              }
              type="edit"
            />
            <SettingRow
              label="Address"
              currentValue={
                userDetailsData.address.street &&
                userDetailsData.address.suburb &&
                userDetailsData.address.postcode &&
                userDetailsData.address.state
                  ? `${
                      userDetailsData.address?.suite
                        ? `${userDetailsData.address.suite}, `
                        : ""
                    } ${userDetailsData.address.street}, ${
                      userDetailsData.address.suburb
                    } (${userDetailsData.address.postcode}), ${
                      userDetailsData.address.state
                    }`
                  : ""
              }
              action={() =>
                openModal({
                  type: "component",
                  heading: "Address",
                  contents: [
                    "Address is optional and only required if you want it displayed on your tax report",
                  ],
                  component: (
                    <AddressForm currentAddress={userDetailsData.address} />
                  ),
                })
              }
              type="edit"
            />
          </SectionContainer>
          {/* ---------------------- Tax Setting ---------------------- */}
          <SectionContainer>
            <SectionHeading>Tax Settings</SectionHeading>
            <SettingRow
              label="Home Country"
              currentValue={userDetailsData.taxSettings.homeCountry}
              action={() => undefined}
            />
            <SettingRow
              label="Base Currency"
              currentValue={userDetailsData.taxSettings.baseCurrency.name}
              action={() => undefined}
            />
            <SettingRow
              label="Tax Year"
              currentValue={userDetailsData.taxSettings.taxYear}
              action={() => undefined}
            />
            {/*
            <SettingRow
              label="GST Registered"
              currentValue={
                userDetailsData.taxSettings.gstRegistered ? "Yes" : "No"
              }
              action={async (event: ChangeEvent<HTMLInputElement>) => {
                try {
                  await updateUser({
                    taxSettings: {
                      homeCountry: userDetailsData.taxSettings.homeCountry,
                      baseCurrency: userDetailsData.taxSettings.baseCurrency,
                      taxYear: userDetailsData.taxSettings.taxYear,
                      gstRegistered: event.currentTarget.checked,
                      parcelMethods: userDetailsData.taxSettings.parcelMethods,
                      timeZone: userDetailsData.taxSettings.timeZone,
                    },
                  });
                  queryClient.invalidateQueries("user-details");
                } catch (error) {
                  // TODO: implement error toast here
                  console.log(error);
                }
              }}
              type="toggle"
            />
            */}
            <SettingRow
              label="Parcel Method"
              // display the method of the last item in parcel methods, last item should always be the latest
              currentValue={defaultParcelMethod}
              action={() =>
                openModal({
                  type: "component",
                  heading: "Parcel Method",
                  hideBottomButton: true,
                  component: (
                    <ParcelMethodForm
                      currentTaxSettings={{
                        ...userDetailsData.taxSettings,
                        parcelMethods: allUsersParcelMethods,
                      }}
                    />
                  ),
                })
              }
              type="edit"
              tip="Selecting the parcel identification method allows you to apply different tax strategies for matching disposals with acquisitions. We recommend our exclusive Lowest-Tax First-Out (LTFO) algorithm for the best tax outcome."
              disabled={!parcelPermission}
            />
            {/*
            <SettingRow
              label="Time Zone"
              currentValue={userDetailsData.taxSettings.timeZone}
              action={() =>
                openModal({
                  type: "component",
                  heading: "Time Zone",
                  component: (
                    <TimeZoneForm
                      currentTaxSettings={userDetailsData.taxSettings}
                    />
                  ),
                })
              }
              type="edit"
              tip="Your selected timezone will be used for all calculations and reports"
            />
            */}
          </SectionContainer>
        </Flex>
        {/* ---------------------- RHS ---------------------- */}
        <Flex
          direction="column"
          w={isBrowser ? { base: "100%", xl: "48%" } : "100%"}
          maxW={isBrowser ? { base: "600px", xl: "800px" } : "600px"}
        >
          {/* ---------------------- Accountant ---------------------- */}

          {/* Commenting for now, will be required later */}

          {/* <SectionContainer>
            <SectionHeading>Accountant</SectionHeading>
            // Map through invited accountants
            {userDetailsData.invitedAccountants.map((accountantEmail) => (
              <SettingRow
                key={accountantEmail}
                label="Invited"
                currentValue={accountantEmail}
                action={async () => {
                  try {
                    await updateUser({
                      invitedAccountants: without(
                        userDetailsData.invitedAccountants,
                        accountantEmail
                      ),
                    });
                    queryClient.invalidateQueries("user-details");
                  } catch (error) {
                    // TODO: implement error toast here
                    console.log(error);
                  }
                }}
                type="remove"
                labelWidth="120px"
              />
            ))}
            // Empty invited accountants message
            {userDetailsData.invitedAccountants.length === 0 && (
              <Text fontSize="0.875rem" color="black.500" mb="15px">
                You have no invited accountants
              </Text>
            )}
          </SectionContainer> */}
          {/* ---------------------- Notifications ---------------------- */}
          {/* !! DO NOT REMOVE THIS, THIS MIGHT COME BACK IN THE FUTURE !! */}
          {/* <SectionContainer>
            <SectionHeading>Notifications</SectionHeading>

            <SettingRow
              label="New transactions"
              currentValue=""
              action={() => undefined}
              type="toggle"
              labelWidth="auto"
            />

            <SettingRow
              label="New transactions - email"
              currentValue=""
              action={() => undefined}
              type="toggle"
              labelWidth="auto"
            />

            <SettingRow
              label="Daily portfolio notifications"
              currentValue=""
              action={() => undefined}
              type="toggle"
              labelWidth="auto"
            />

            <SettingRow
              label="Daily portfolio - email"
              currentValue=""
              action={() => undefined}
              type="toggle"
              labelWidth="auto"
            />

            <SettingRow
              label="Product updates and promotional emails"
              currentValue=""
              action={() => undefined}
              type="toggle"
              labelWidth="auto"
            />
          </SectionContainer> */}
          {/* ---------------------- Plan ---------------------- */}
          <SectionContainer>
            <SectionHeading>Subscriptions</SectionHeading>
            <Flex direction="row" justifyContent="space-between">
              <SettingsSubscriptionStatus
                subscriptions={userDetailsData?.subscriptions ?? []}
              />
            </Flex>
          </SectionContainer>
          {/* ---------------------- Support Access ---------------------- */}
          <Flex direction="row" mt="30px" justifyContent="space-between">
            <Flex direction="column" mb="20px" w="70%">
              <SettingLabel mb="20px">Support Access</SettingLabel>
              <Text fontSize="0.875rem" color="black.500">
                Allow the Syla support team to access your account in order to
                assist with support requests.
              </Text>
            </Flex>
            <Btn
              justifySelf="end"
              onClick={() =>
                updateUserRequest({
                  supportAccess: !userDetailsData?.supportAccess,
                }).then(() => {
                  toast({
                    position: "top",
                    duration: 1000,
                    ...(!userDetailsData?.supportAccess
                      ? {
                          status: "success",
                          title: "Support Access Granted",
                        }
                      : {
                          status: "warning",
                          title: "Support Access Revoked",
                        }),
                  });
                })
              }
              isLoading={updateUserInProgress || userDetailsStale}
            >
              {userDetailsData?.supportAccess
                ? "DISABLE SUPPORT ACCESS"
                : "ENABLE SUPPORT ACCESS"}
            </Btn>
          </Flex>
          {/* ---------------------- Delete Account ---------------------- */}
          <Flex direction="row" mt="30px" justifyContent="space-between">
            <Flex direction="column" mb="20px" w="70%">
              <SettingLabel mb="20px">Delete Account</SettingLabel>
              <Text fontSize="0.875rem" color="black.500">
                This deletes all your Syla account data including exchanges,
                wallets, transactions and trade history. This action is
                irreversible.
              </Text>
            </Flex>
            <Btn
              justifySelf="end"
              onClick={() => {
                openModal({
                  type: "confirmation",
                  icon: "delete",
                  heading: "Are you sure you want to delete this account?",
                  contents: [
                    "This action is irreversible and will delete all of your transaction and tax history.",
                    "You can always sign up again later, but your purchase history will also be lost.",
                  ],
                  btn2Content: "Delete",
                  btn2Action: async () => {
                    try {
                      setBtn2Loading(true);
                      await deleteUser();
                      openModal({
                        type: "action",
                        icon: "delete",
                        heading: "Account Deleted",
                        btn1Content: "Done",
                        btn1Action: () => logout(),
                        hideBottomButton: true,
                        dontCloseOnOverlayClick: true,
                      });
                      setBtn2Loading(false);
                    } catch (error) {
                      // TODO: implement error toast here
                      console.log(error);
                    }
                  },
                });
              }}
            >
              DELETE ACCOUNT
            </Btn>
          </Flex>
          {!isProduction() && (
            <Flex direction="row" mt="30px" justifyContent="space-between">
              <SettingLabel mb="20px">Reset Account (Testing)</SettingLabel>
              <Btn
                onClick={() =>
                  openModal({
                    type: "confirmation",
                    icon: "delete",
                    heading: "Are you sure you want to reset the account?",
                    contents: ["This action is irreversible."],
                    btn2Content: "Reset",
                    btn2Action: async () => {
                      try {
                        setBtn2Loading(true);
                        await resetUser();
                        resetTransactionStore();
                        setBtn2Loading(false);
                        closeModal();
                      } catch (error) {
                        // TODO: implement error toast here
                        console.log(error);
                      }
                    },
                  })
                }
              >
                <Image
                  height="25px"
                  mr="10px"
                  src="https://cdn1.iconfinder.com/data/icons/editing/60/cell-12-2-480.png"
                />
                RESET ACCOUNT
              </Btn>
            </Flex>
          )}
        </Flex>
      </Flex>
    </PageContainer>
  );
};

const SectionHeading = styled(Text)`
  color: #666;
  font-size: 1.125rem;
  font-weight: bold;
  margin-bottom: 20px;
`;

export const SettingLabel = styled(Text)`
  color: #4d4d4d;
  font-size: 1rem;
`;

const CurrentValueText = styled(Text)`
  color: #808080;
  font-size: 1rem;
`;

const SectionContainer = styled(Flex)`
  flex-direction: column;
  margin-bottom: 20px;
`;

const Btn = styled(Button)`
  color: #808080;
  background-color: #fff;
  font-size: 0.875rem;
  font-weight: 400;
  height: 32px;
  border: 1px solid #e5e5e5;
  text-transform: uppercase;
  &:hover {
    background-color: #f5f5f5;
  }
`;
